import React, { useCallback, useMemo } from 'react';
import { Pressable } from 'react-native';
import { Chat, IOStatusEnum } from '@matap/shared';
import styled from 'styled-components/native';
import { useFileAsset } from '@/hooks/use.file.asset';
import { useScreenSize } from '@/hooks/use.screen.size';
import { AppImageView } from '@/components/base/app.image.view';
import { Box } from '@/components/base/box';
import { ChatFooterView } from './chat.footer.view';
import { DownloadUploadStateView } from './download.upload.state.view';
import { ChatContainer } from '@/components/compose/chat/chat.container';
import { generateShadow } from '@/components/base/app.card.view';
import { hpVal, scaleImage } from '../../../utils';
import { useAppRouter } from '@/hooks/use.app.router';
import { ModalNamesEnum } from '@/constants/enums/modal.names.enum';

interface Props {
  chat: Chat;
  isSentByUser: boolean;
  temporary: boolean;
  onLongPress: (e: any) => void;
}
function BaseChatImageView({ chat, isSentByUser, temporary, onLongPress }: Props) {
  const { state, mediaMetaData } = useFileAsset(chat, temporary);
  const { wp, hp } = useScreenSize();
  const { navigateTo } = useAppRouter();
  const { width, height } = useMemo(
    () => scaleImage(mediaMetaData.width, mediaMetaData.height, Math.min(wp(70), 300), hp(50)),
    [hp, mediaMetaData.height, mediaMetaData.width, wp],
  );

  const onPress = useCallback(() => {
    if (state?.localAddress) {
      navigateTo(ModalNamesEnum.ImageZoom, { imagePath: state?.localAddress });
    }
  }, [state?.localAddress, navigateTo]);

  return (
    <ChatContainer isSentByUser={isSentByUser} onLongPress={onLongPress}>
      <Pressable onPress={onPress} onLongPress={onLongPress}>
        <Image
          style={generateShadow({ shadowColor: '#000', shadowOpacity: 0.2, blurRadius: 8, shadowOffsetX: 0, shadowOffsetY: 2 })}
          source={{ uri: state?.localAddress }}
          width={width}
          height={height}
        />
      </Pressable>
      {state && state.state !== IOStatusEnum.READY && (
        <IOStatusContainer>
          <IOStatusView fileState={state} chatId={chat._id} />
        </IOStatusContainer>
      )}

      <ChatFooterView chat={chat} isSentByUser={isSentByUser} />
    </ChatContainer>
  );
}

const Image = styled(AppImageView)<{ width: number; height: number }>`
  width: ${({ width }) => width}px;
  height: ${({ height }) => height}px;
  border-radius: ${hpVal(1)};
  overflow: hidden;
`;

const IOStatusContainer = styled(Box)`
  position: absolute;
  align-items: center;
  justify-content: center;
  right: 0;
  left: 0;
  bottom: 0;
  top: 0;
`;

const IOStatusView = styled(DownloadUploadStateView)`
  background-color: rgba(0, 0, 0, 0.28);
  border-radius: 999px;
`;

export const ChatImageView = React.memo(BaseChatImageView);
